import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import ConfirmationDialogBox from 'components/ConfirmationDialogBox';
import Icon from 'components/DesignSystem/Icon';
import Snackbar from 'components/DesignSystem/Snackbar';
import PersonalizedScenario from 'components/Flux/Personalized/Row/Scenario';
import useStudyRowActions from 'components/Flux/Personalized/Row/useStudyRowActions';
import { isStudyFinished, isStudyRunning, ListedStudy } from 'helper/studies';
import { selectUserId, selectUserIsAdmin } from 'redux/reducers/authReducer';
import { useReexecuteMutation } from 'services/api/products/flux';
import usePersonalizedDownload from 'utils/hooks/flux/personalized/usePersonalizedDownload';
import { cssVar } from 'constants/theme';

import 'components/Flux/Upload/Mobile/StudyCard/index.scss';
import './index.scss';

const STATUS_MODIFIER: Record<string, string> = {
  'Em Fila': 'queued',
  'Com Erro': 'error',
  'Estudo Pronto': 'ready',
  Cancelado: 'cancelled',
};

function formatDateTime(timestamp: number | null, fallback: string): string {
  if (!timestamp) {
    return fallback;
  }

  return new Date(timestamp * 1000).toLocaleString('pt-BR');
}

export default function PersonalizedStudyCard({ study }: { study: ListedStudy }) {
  const [scenariosOpen, setScenariosOpen] = useState(false);
  const [reexecutionOpen, setReexecutionOpen] = useState(false);
  const [triggerReexecute] = useReexecuteMutation();
  const {
    download, isLoading: downloading, disabled: downloadDisabled,
  } = usePersonalizedDownload(study);
  const {
    confirmationDialogBox, feedback, clearFeedback,
    confirmDelete, confirmCancel, deleting, cancelling,
  } = useStudyRowActions(study);
  const userId = useSelector(selectUserId);
  const isAdmin = useSelector(selectUserIsAdmin);
  // O backend decide a posse; aqui só não oferecemos a ação a quem não é o dono
  const isOwner = isAdmin || (study.id_user !== null && study.id_user === userId);
  const running = isStudyRunning(study);
  // Com Cancelar na tela são quatro botões: Cancelar/Excluir ficam só no ícone
  const showCancel = isOwner && !isStudyFinished(study);
  const ownerButtonClass = `upload-study-card-action danger${showCancel ? ' icon-only' : ''}`;

  const horizonStart = new Date(study.dt_inicio * 1000).toLocaleDateString('pt-BR');
  const horizonEnd = new Date(study.dt_fim * 1000).toLocaleDateString('pt-BR');
  const lastExecution = formatDateTime(study.dt_last_execution, 'Não executado');
  const lastResponse = formatDateTime(study.dt_last_response, 'Não houve resposta');
  const scenariosId = `personalized-scenarios-${study.id}`;

  return (
    <article className="upload-study-card personalized-study-card">
      <ConfirmationDialogBox
        open={reexecutionOpen}
        dismiss={() => { setReexecutionOpen(false); }}
        confirm={() => { triggerReexecute(study.id); }}
      >
        <div>
          Você tem certeza de que deseja reenviar este estudo (
          <b>{study.ds_nome_estudo}</b>
          ) ?
        </div>
      </ConfirmationDialogBox>
      {confirmationDialogBox}
      {feedback && (
        <Snackbar
          className="m-2"
          variant={feedback.variant}
          closeButton
          onClose={clearFeedback}
        >
          {feedback.message}
        </Snackbar>
      )}

      <div className="upload-study-card-description">
        <h2 className="upload-study-card-name">{study.ds_nome_estudo}</h2>
        <p className="upload-study-card-company">{study.empresa}</p>
        {study.user && (
          <div className="upload-study-card-chips">
            <span className="upload-study-card-chip">
              <Icon iconType="user" color={cssVar('text-muted')} size="small" />
              {study.user}
            </span>
          </div>
        )}
      </div>

      <div className="upload-study-card-horizon">
        <Icon iconType="calendar" color={cssVar('text-accent')} size="small" />
        <span className="upload-study-card-horizon-label">Horizonte</span>
        <span className="upload-study-card-horizon-value">
          {horizonStart}
          {' – '}
          {horizonEnd}
        </span>
      </div>

      <div className="upload-study-card-status">
        <span className={`upload-study-card-tag ${STATUS_MODIFIER[study.id_status] ?? ''}`}>
          <span className="upload-study-card-dot" />
          {study.id_status}
        </span>

        {study.ck_internal_error && (
          <div className="upload-study-card-warning">
            <Icon iconType="warning" color={cssVar('danger-fg')} size="small" />
            <span>
              Verifique seu e-mail de processamento interrompido para mais informações
            </span>
          </div>
        )}

        <dl className="upload-study-card-meta">
          <div>
            <dt>Última Execução</dt>
            <dd>{lastExecution}</dd>
          </div>
          <div>
            <dt>Última Resposta</dt>
            <dd>{lastResponse}</dd>
          </div>
          <div>
            <dt>Nº de Execuções</dt>
            <dd>{study.vl_execution_counter}</dd>
          </div>
        </dl>
      </div>

      <div className="personalized-study-card-scenarios-box">
        <button
          type="button"
          className="personalized-study-card-scenarios-toggle"
          aria-expanded={scenariosOpen}
          aria-controls={scenariosId}
          onClick={() => { setScenariosOpen((open) => !open); }}
        >
          {`Cenários (${study.cenarios.length})`}
          <span className="personalized-study-card-chevron" aria-hidden="true" />
        </button>
        {/* Só monta aberto: cada bloco dispara consultas de opções de modelo */}
        {scenariosOpen && (
          <div id={scenariosId} className="personalized-study-card-scenarios">
            {study.cenarios.map((scenario) => (
              <PersonalizedScenario scenario={scenario} key={scenario.id.toString()} />
            ))}
          </div>
        )}
      </div>

      <div className="upload-study-card-actions">
        <button
          type="button"
          id={`download-action-${study.id}`}
          className={`upload-study-card-action${downloading ? ' loading' : ''}`}
          disabled={downloadDisabled || downloading}
          aria-busy={downloading}
          onClick={download}
        >
          <Icon iconType="download" color={cssVar('text-strong')} size="small" />
          {downloading ? 'Baixando…' : 'Baixar'}
        </button>
        <button
          type="button"
          id={`reruns-study-${study.id}`}
          className="upload-study-card-action accent"
          onClick={() => { setReexecutionOpen(true); }}
        >
          <Icon iconType="refresh" color={cssVar('text-accent')} size="small" />
          Re-executar
        </button>
        {showCancel && (
          <button
            type="button"
            id={`cancel-study-${study.id}`}
            className={ownerButtonClass}
            disabled={cancelling}
            aria-label="Cancelar o estudo"
            title="Cancele a execução do estudo"
            onClick={confirmCancel}
          >
            <Icon iconType="close" color={cssVar('danger-fg')} size="small" />
          </button>
        )}
        {isOwner && (
          <button
            type="button"
            id={`delete-study-${study.id}`}
            className={ownerButtonClass}
            disabled={running || deleting}
            aria-label="Excluir o estudo"
            title={running ? 'Cancele o estudo antes de removê-lo' : 'Remova o estudo'}
            onClick={confirmDelete}
          >
            <Icon iconType="trash" color={cssVar('danger-fg')} size="small" />
            {!showCancel && 'Excluir'}
          </button>
        )}
      </div>
      {isOwner && running && (
        <p className="upload-study-card-hint">Cancele o estudo antes de removê-lo</p>
      )}
    </article>
  );
}
